import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import DashboardLayout from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { 
  CreditCard, Users, GraduationCap, Download, Printer,
  User, Building, Search, Settings, ExternalLink
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const IdCardsPage = () => {
  const { getAuthHeader } = useAuth();
  const [activeTab, setActiveTab] = useState('students');
  const [loading, setLoading] = useState(false);
  
  const [classes, setClasses] = useState([]);
  const [selectedClass, setSelectedClass] = useState('');
  const [students, setStudents] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [staff, setStaff] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [savedTemplate, setSavedTemplate] = useState(null);
  const [allTemplates, setAllTemplates] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [classesRes, studentsRes, teachersRes, staffRes, templatesRes] = await Promise.all([
        axios.get(`${API_URL}/api/classes`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/students`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/teachers`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/staff`, { headers: getAuthHeader() }).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/api/id-card-templates`, { headers: getAuthHeader() }).catch(() => ({ data: [] }))
      ]);
      setClasses(classesRes.data);
      setStudents(studentsRes.data?.items || studentsRes.data || []);
      // Handle paginated response for teachers
      setTeachers(teachersRes.data?.items || teachersRes.data || []);
      setStaff(staffRes.data?.items || staffRes.data || []);
      
      // Store all templates
      const templates = templatesRes.data || [];
      setAllTemplates(templates);
      
      // Use the latest saved template with elements if available
      const templatesWithElements = templates.filter(t => t.elements && t.elements.length > 0);
      if (templatesWithElements.length > 0) {
        setSavedTemplate(templatesWithElements[templatesWithElements.length - 1]);
      } else if (templates.length > 0) {
        setSavedTemplate(templates[templates.length - 1]);
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load data');
    }
  };

  const downloadIdCard = async (type, id, name) => {
    setLoading(true);
    try {
      // If we have a saved template, use the custom generator
      if (savedTemplate && type === 'student') {
        const response = await axios.post(
          `${API_URL}/api/id-card-templates/generate`,
          { template: savedTemplate, student_ids: [id] },
          { headers: getAuthHeader(), responseType: 'blob' }
        );
        
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `id_card_${name.replace(/\s+/g, '_')}.pdf`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } else {
        // Fallback to default ID card
        const response = await axios.get(
          `${API_URL}/api/id-cards/${type}/${id}`,
          { headers: getAuthHeader(), responseType: 'blob' }
        );
        
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `id_card_${name.replace(/\s+/g, '_')}.pdf`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      }
      
      toast.success('ID Card downloaded!');
    } catch (error) {
      toast.error('Failed to generate ID card');
    } finally {
      setLoading(false);
    }
  };

  const downloadBulkIdCards = async (type, classId = null) => {
    setLoading(true);
    try {
      // If we have a saved template and it's students, use custom generator
      if (savedTemplate && type === 'students') {
        const studentIds = classId 
          ? students.filter(s => s.class_id === classId).map(s => s.id)
          : students.map(s => s.id);
        
        const response = await axios.post(
          `${API_URL}/api/id-card-templates/generate`,
          { template: savedTemplate, student_ids: studentIds },
          { headers: getAuthHeader(), responseType: 'blob' }
        );
        
        const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = blobUrl;
        link.setAttribute('download', `id_cards_custom_${classId || 'all'}.pdf`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(blobUrl);
      } else {
        // Fallback to default
        let url = `${API_URL}/api/id-cards/${type}/bulk`;
        if (classId) {
          url += `?class_id=${classId}`;
        }
        
        const response = await axios.get(url, { 
          headers: getAuthHeader(),
          responseType: 'blob'
        });
        
        const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = blobUrl;
        link.setAttribute('download', `id_cards_${type}_${classId || 'all'}.pdf`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(blobUrl);
      }
      
      toast.success('ID Cards downloaded!');
    } catch (error) {
      toast.error('Failed to generate ID cards');
    } finally {
      setLoading(false);
    }
  };

  const filteredStudents = students.filter(s => {
    const matchesClass = !selectedClass || s.class_id === selectedClass;
    const matchesSearch = !searchQuery || 
      s.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      s.student_id?.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesClass && matchesSearch;
  });

  const filteredTeachers = teachers.filter(t => 
    !searchQuery || 
    t.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    t.employee_id?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredStaff = staff.filter(s => 
    !searchQuery || 
    s.name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <DashboardLayout title="ID Card Generation">
      <div className="space-y-6">
        {/* Header Stats */}
        <div className="grid grid-cols-3 gap-4">
          <Card>
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <GraduationCap className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{students.length}</p>
                <p className="text-xs text-muted-foreground">Students</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <User className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{teachers.length}</p>
                <p className="text-xs text-muted-foreground">Teachers</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Building className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{staff.length}</p>
                <p className="text-xs text-muted-foreground">Staff</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Template Status Banner */}
        <Card className={savedTemplate ? "border-green-500 bg-green-50 dark:bg-green-950/30" : "border-amber-500 bg-amber-50 dark:bg-amber-950/30"}>
          <CardContent className="p-4 flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-3">
              <Settings className={`h-5 w-5 ${savedTemplate ? 'text-green-600' : 'text-amber-600'}`} />
              <div>
                <p className="font-medium text-sm">
                  {savedTemplate ? `Active: ${savedTemplate.name || 'Custom Design'}` : 'Using Default ID Card Design'}
                </p>
                <p className="text-xs text-muted-foreground">
                  {savedTemplate?.elements?.length > 0 
                    ? `${savedTemplate.elements.length} elements • ${savedTemplate.orientation || 'landscape'}` 
                    : 'Create a custom template in ID Card Builder'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {allTemplates.length > 0 && (
                <Select 
                  value={savedTemplate?.id || ''} 
                  onValueChange={(v) => {
                    if (v === 'default') {
                      setSavedTemplate(null);
                    } else {
                      const template = allTemplates.find(t => t.id === v);
                      setSavedTemplate(template);
                    }
                  }}
                >
                  <SelectTrigger className="w-[200px]" data-testid="template-select">
                    <SelectValue placeholder="Select Template" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="default">Default Template</SelectItem>
                    {allTemplates.map(t => (
                      <SelectItem key={t.id} value={t.id}>
                        {t.name} {t.elements?.length > 0 ? `(${t.elements.length})` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              <Link to="/id-card-builder">
                <Button variant="outline" size="sm">
                  <ExternalLink className="h-4 w-4 mr-1" />
                  {savedTemplate ? 'Edit' : 'Create'}
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="students" data-testid="students-tab">
              <GraduationCap className="h-4 w-4 mr-1" /> Students
            </TabsTrigger>
            <TabsTrigger value="teachers" data-testid="teachers-tab">
              <User className="h-4 w-4 mr-1" /> Teachers
            </TabsTrigger>
            <TabsTrigger value="staff" data-testid="staff-tab">
              <Building className="h-4 w-4 mr-1" /> Staff
            </TabsTrigger>
          </TabsList>

          {/* Students Tab */}
          <TabsContent value="students" className="space-y-4">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Student ID Cards</CardTitle>
                    <CardDescription>Generate and download student identity cards</CardDescription>
                  </div>
                  <div className="flex gap-2">
                    <Select value={selectedClass || "all"} onValueChange={(v) => setSelectedClass(v === "all" ? "" : v)}>
                      <SelectTrigger className="w-40">
                        <SelectValue placeholder="All Classes" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Classes</SelectItem>
                        {classes.map(c => (
                          <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button 
                      onClick={() => downloadBulkIdCards('students', selectedClass || null)}
                      disabled={loading}
                      data-testid="download-all-students"
                    >
                      <Download className="h-4 w-4 mr-2" />
                      {selectedClass ? 'Download Class' : 'Download All'}
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="mb-4">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Search by name or ID..."
                      value={searchQuery}
                      onChange={e => setSearchQuery(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                </div>

                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-muted/50">
                      <tr>
                        <th className="p-3 text-left text-sm">Student</th>
                        <th className="p-3 text-left text-sm">ID</th>
                        <th className="p-3 text-left text-sm">Class</th>
                        <th className="p-3 text-left text-sm">Father</th>
                        <th className="p-3 text-center text-sm">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredStudents.slice(0, 20).map(student => (
                        <tr key={student.id} className="border-t">
                          <td className="p-3">
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                                <User className="h-4 w-4 text-blue-600" />
                              </div>
                              <span className="font-medium">{student.name}</span>
                            </div>
                          </td>
                          <td className="p-3 font-mono text-sm">{student.student_id}</td>
                          <td className="p-3">
                            <Badge variant="outline">{student.class_name}</Badge>
                          </td>
                          <td className="p-3 text-sm text-muted-foreground">{student.father_name}</td>
                          <td className="p-3 text-center">
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => downloadIdCard('student', student.id, student.name)}
                              disabled={loading}
                              data-testid={`download-${student.id}`}
                            >
                              <CreditCard className="h-4 w-4 mr-1" /> ID Card
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {filteredStudents.length > 20 && (
                  <p className="text-sm text-muted-foreground mt-2 text-center">
                    Showing 20 of {filteredStudents.length} students. Use bulk download for all.
                  </p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Teachers Tab */}
          <TabsContent value="teachers" className="space-y-4">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Teacher ID Cards</CardTitle>
                    <CardDescription>Generate identity cards for teaching staff</CardDescription>
                  </div>
                  <Button 
                    onClick={() => downloadBulkIdCards('teachers')}
                    disabled={loading}
                    data-testid="download-all-teachers"
                  >
                    <Download className="h-4 w-4 mr-2" /> Download All Teachers
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-muted/50">
                      <tr>
                        <th className="p-3 text-left text-sm">Teacher</th>
                        <th className="p-3 text-left text-sm">Employee ID</th>
                        <th className="p-3 text-left text-sm">Subject</th>
                        <th className="p-3 text-left text-sm">Phone</th>
                        <th className="p-3 text-center text-sm">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredTeachers.map(teacher => (
                        <tr key={teacher.id} className="border-t">
                          <td className="p-3">
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
                                <User className="h-4 w-4 text-green-600" />
                              </div>
                              <span className="font-medium">{teacher.name}</span>
                            </div>
                          </td>
                          <td className="p-3 font-mono text-sm">{teacher.employee_id || teacher.id?.slice(0, 8)}</td>
                          <td className="p-3">
                            <Badge variant="secondary">{teacher.subject || 'N/A'}</Badge>
                          </td>
                          <td className="p-3 text-sm">{teacher.phone}</td>
                          <td className="p-3 text-center">
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => downloadIdCard('teacher', teacher.id, teacher.name)}
                              disabled={loading}
                            >
                              <CreditCard className="h-4 w-4 mr-1" /> ID Card
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Staff Tab */}
          <TabsContent value="staff" className="space-y-4">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Non-Teaching Staff ID Cards</CardTitle>
                    <CardDescription>Generate identity cards for administrative and support staff</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {filteredStaff.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Building className="h-12 w-12 mx-auto mb-3 opacity-50" />
                    <p>No non-teaching staff found</p>
                  </div>
                ) : (
                  <div className="border rounded-lg overflow-hidden">
                    <table className="w-full">
                      <thead className="bg-muted/50">
                        <tr>
                          <th className="p-3 text-left text-sm">Staff</th>
                          <th className="p-3 text-left text-sm">Category</th>
                          <th className="p-3 text-left text-sm">Designation</th>
                          <th className="p-3 text-left text-sm">Phone</th>
                          <th className="p-3 text-center text-sm">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredStaff.map(s => (
                          <tr key={s.id} className="border-t">
                            <td className="p-3">
                              <div className="flex items-center gap-3">
                                <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center">
                                  <User className="h-4 w-4 text-purple-600" />
                                </div>
                                <span className="font-medium">{s.name}</span>
                              </div>
                            </td>
                            <td className="p-3 text-sm">{s.category}</td>
                            <td className="p-3">
                              <Badge variant="outline">{s.designation}</Badge>
                            </td>
                            <td className="p-3 text-sm">{s.phone}</td>
                            <td className="p-3 text-center">
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => downloadIdCard('staff', s.id, s.name)}
                                disabled={loading}
                              >
                                <CreditCard className="h-4 w-4 mr-1" /> ID Card
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
};

export default IdCardsPage;
